import type { IntegrationConnectionStatus } from './company';

export type { IntegrationConnectionStatus };

export type FinanceProduct = 'access-financials' | 'access-dimensions' | 'other';
export type TimesheetProduct = 'access-time' | 'access-focalpoint' | 'access-financial-operations' | 'other';
export type ExpenseProduct = 'access-expense' | 'other';
export type PlannerProduct = 'access-planner' | 'resource-scheduler' | 'other';
export type PurchasingProduct = 'access-financial-operations-purchasing' | 'access-focalpoint-purchasing' | 'other';

export const FINANCE_PRODUCT_LABELS: Record<FinanceProduct, string> = {
  'access-financials': 'Access Financials',
  'access-dimensions': 'Access Dimensions',
  other: 'Other',
};

export const TIMESHEET_PRODUCT_LABELS: Record<TimesheetProduct, string> = {
  'access-time': 'Access Time',
  'access-focalpoint': 'Access FocalPoint',
  'access-financial-operations': 'Access Financial Operations',
  other: 'Other',
};

export const EXPENSE_PRODUCT_LABELS: Record<ExpenseProduct, string> = {
  'access-expense': 'Access Expense',
  other: 'Other',
};

export const PLANNER_PRODUCT_LABELS: Record<PlannerProduct, string> = {
  'access-planner': 'Access Planner',
  'resource-scheduler': 'Resource Scheduler',
  other: 'Other',
};

export const PURCHASING_PRODUCT_LABELS: Record<PurchasingProduct, string> = {
  'access-financial-operations-purchasing': 'Access Financials Operations \\ Access Financials',
  'access-focalpoint-purchasing': 'Access FocalPoint \\ Access Dimensions',
  other: 'Other',
};

export interface SimpleIntegrationConfig {
  product: FinanceProduct | TimesheetProduct | ExpenseProduct | PlannerProduct | PurchasingProduct | null;
  databaseId: string | null;
  /** Only used by purchasing — stores the matched Financials Operations instance ID */
  finOpsInstanceId?: string | null;
  status: IntegrationConnectionStatus;
  lastSyncAt: string | null;
}

export interface CompanyWithIntegrations {
  id: number;
  name: string;
  code: string;
  status: 'active' | 'inactive';
  financeIntegration: SimpleIntegrationConfig;
  timesheetIntegration: SimpleIntegrationConfig;
  expenseIntegration: SimpleIntegrationConfig;
  plannerIntegration: SimpleIntegrationConfig;
  purchasingIntegration: SimpleIntegrationConfig;
  userCount: number;
}

export interface CompanyUser {
  id: string;
  name: string;
  email: string;
  role: string;
  photoUrl?: string;
  isDefault: boolean;
}

export interface EmployeeCompanyAccessRow {
  companyId: number;
  companyName: string;
  companyCode: string;
  companyStatus: 'active' | 'inactive';
  hasAccess: boolean;
  isDefault: boolean;
}

export interface UpdateIntegrationsPayload {
  finance?: { product: FinanceProduct | null; databaseId: string | null };
  timesheet?: { product: TimesheetProduct | null; databaseId: string | null };
  expense?: { product: ExpenseProduct | null; databaseId: string | null };
  planner?: { product: PlannerProduct | null; databaseId: string | null };
  purchasing?: { product: PurchasingProduct | null; databaseId: string | null; finOpsInstanceId?: string | null };
}

export interface TestIntegrationResponse {
  success: boolean;
  message: string;
  testedAt: string;
}

export interface AvailableDatabase {
  id: string;       // the value stored in databaseId
  label: string;    // human-readable name shown in the dropdown
  environment?: string; // e.g. 'Production', 'Staging', 'UAT'
}
